import clsx from "clsx";
import {
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
  useCallback,
  useRef,
  DragEvent,
  ChangeEvent,
} from "react";

import TextareaAutosize from "react-textarea-autosize";

import { InfoIcon, LaunchIcon, CloseIcon, RefreshIcon } from "components/icons";

import { UploadAttach } from "./UploadAttach"; //MAX_IMAGES
import { ExtraAction } from "./ExtraAction";
import DropdownComponent from "./DropdownComponent";
import { DragCard } from "./DragCard";
import LoadingSpinner from "components/LoadingSpinner";
import type { IAspectRatioe, ISampleCount } from "./ExtraAction";

import { imageGenerationCategories } from "data/image-categories";
import { IImageStyle, imageStyles } from "data/attach-styles";
import type { StoredFile } from "hooks/useProcessFiles";
import { getRandomItem } from "utils/function";
import { CREDITS_PER_IMAGE } from "utils/creditUtils";
import { IFetchErrorMessage } from "hooks/useGenerateImage";

type IPromptContainerProps = {
  promptContainerRef: React.RefObject<HTMLDivElement | null>;
  selectedImageStyleIndex: number;
  selectedFiles: Record<string, StoredFile>;
  message: string;
  isOpen: boolean;
  selectedItem: IImageStyle[];
  // displayedItems: IImageStyle[];
  aspectRatio: IAspectRatioe;
  sampleCount: ISampleCount;
  isLoading?: boolean;
  fetchErrorMessage?: IFetchErrorMessage;
  disabled: boolean;
  isOverMaxCharLength?: boolean;
  acceptString?: string;
  isEditingImage?: boolean;
  isNeedReload?: boolean;
  setIsNeedReload: Dispatch<SetStateAction<boolean>>;
  setAspectRatio: Dispatch<SetStateAction<IAspectRatioe>>;
  setSampleCount: Dispatch<SetStateAction<ISampleCount>>;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setSelectedFiles: Dispatch<SetStateAction<Record<string, StoredFile>>>;
  onRemovedCallback?: (fileId: string) => void;
  setSelectedItem: Dispatch<SetStateAction<IImageStyle[]>>;
  // setDisplayedItems: Dispatch<SetStateAction<IImageStyle[]>>;
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  processFiles: (
    files: FileList,
    event?: ChangeEvent<HTMLInputElement>
  ) => void;
  removeFile: (fileId: string) => void;
  onFetch: () => void;
  onTestFetch?: () => void;
  onTestUpgrade?: () => void;
};

const PromptContainer: React.FC<IPromptContainerProps> = ({
  promptContainerRef,
  message,
  isOpen,
  selectedImageStyleIndex,
  selectedFiles,
  selectedItem,
  aspectRatio,
  isLoading,
  disabled,
  isOverMaxCharLength,
  sampleCount,
  isEditingImage,
  onTestFetch,
  // onTestUpgrade,
  acceptString,
  isNeedReload,
  onFetch,
  setIsOpen,
  setIsNeedReload,
  setAspectRatio,
  setSampleCount,
  // setSelectedFiles,
  onRemovedCallback,
  handleChange,
  setSelectedItem,
  handleKeyDown,
  processFiles,
  removeFile,
}) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState(false);

  const dragCounter = useRef<number>(0);

  // 页面上展示的 6 个对象
  const [displayedItems, setDisplayedItems] = useState<IImageStyle[]>([]);

  // 初始化展示 6 个对象
  useEffect(() => {
    const initialDisplayed = imageStyles.slice(0, 6);
    setDisplayedItems(initialDisplayed);
  }, []);

  // 计算剩余对象（从 allStyles 中过滤掉 displayedStyles 和 selectedValues 对应的对象）
  const getRemainingStyles = (): IImageStyle[] => {
    return imageStyles.filter(
      (style) =>
        // 排除 displayedStyles 中的对象
        !displayedItems.some((displayed) => displayed.label === style.label) &&
        // 排除 selectedValues 中的对象
        !selectedItem.some((selected) => selected.label === style.label)
    );
  };

  // 处理选中事件
  const handleSelect = (index: number, item: IImageStyle) => {
    // 从展示数组中移除选中的对象
    const updatedDisplayed = displayedItems.filter(
      (style) => style.label !== item.label
    );

    // 从剩余对象中随机取一个对象补充
    const remainingStyles = getRemainingStyles();
    const randomStyle = getRandomItem(remainingStyles);
    if (randomStyle) {
      updatedDisplayed.push(randomStyle);
    }

    // 更新展示数组
    setDisplayedItems(updatedDisplayed);
  };

  // 更新dropdown
  const onChangeDropdown = (
    index: number,
    item: IImageStyle,
    value: string
  ) => {
    // 如果当前的item不存在 => 将item值写入selectedItem,并将值设置为value,否则直接更改里面的值
    setSelectedItem((prevItems) => {
      const exists = prevItems.some((style) => style.label === item.label);
      if (!exists) {
        return [...prevItems, { ...item, value }]; // 不更新状态
      }
      return prevItems.map((style) =>
        style.label === item.label ? { ...style, value } : style
      );
    });
    handleSelect(index, item);
  };

  // 加载更多 从剩余的imageStyle中随机取6个值
  const handleLoadMore = () => {
    const initialDisplayed = getRemainingStyles().slice(0, 6);
    setDisplayedItems(initialDisplayed);
  };

  // 处理移除值的逻辑
  const onRemovedItem = (item: IImageStyle, index: number) => {
    if (!displayedItems.some((style) => style.label === item.label)) {
      // 插入到展示数组开头
      let updatedDisplayed = [{ ...item, value: "" }, ...displayedItems];

      // 如果展示数组超过 6 个，移除末尾多余的对象
      if (updatedDisplayed.length > 6) {
        updatedDisplayed = updatedDisplayed.slice(0, 6);
      }

      // 更新展示数组
      setDisplayedItems(updatedDisplayed);

      // 移除所选的选项
      setSelectedItem((prevSelected) => {
        const updatedSelected = prevSelected.filter((_, i) => i !== index);
        return updatedSelected;
      });
    }
  };

  useEffect(() => {
    if (isNeedReload) {
      handleLoadMore();
      setIsNeedReload(false);
    }
    // eslint-disable-next-line
  }, [isNeedReload, selectedItem, setIsNeedReload]);

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  // 防止默认拖放行为
  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  // 处理拖放事件
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;
      const files = e.dataTransfer.files;
      if (files) {
        processFiles(files);
      }
    },
    [processFiles]
  );

  return (
    <div
      className={clsx(
        "bg-white p-2 md:p-4 rounded-lg shadow mb-4 border-[0.6px] border-solid mt-3 border-[#94A3B8]"
      )}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      ref={promptContainerRef}
    >
      {isDragging ? (
        <DragCard />
      ) : (
        <>
          <h2 className="text-lg font-semibold">
            {imageGenerationCategories[selectedImageStyleIndex].name}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            {imageGenerationCategories[selectedImageStyleIndex].perfectFor}
          </p>

          {/* Prompt Input */}
          <div className="bg-white rounded-lg">
            <div className="px-3 mb-6">
              <UploadAttach
                processFiles={processFiles}
                acceptString={acceptString}
                selectedFiles={selectedFiles}
                // setSelectedFiles={setSelectedFiles}
                onRemovedCallback={onRemovedCallback}
                removeFile={removeFile}
              />

              <div
                className={clsx(
                  "relative w-full border-[0.8px] border-solid rounded-[8px] pt-3 pb-2 bg-[rgba(226,232,240,0.25)]",
                  {
                    "border-[#94A3B8]": !message && !isFocused,
                    "border-[#4338CA]": message || isFocused,
                    "border-[#DC2626]": isOverMaxCharLength,
                  }
                )}
              >
                <TextareaAutosize
                  minRows={2}
                  maxRows={7}
                  value={message}
                  className="w-full resize-none outline-none border-none font-inter text-[#171717] text-[16px] bg-transparent leading-5 placeholder:text-[#737373] px-3"
                  placeholder={
                    isEditingImage
                      ? "Convert a this portrait with similar style as the sketch attached"
                      : "A professional portrait of a middle-aged man in a navy suit against a neutral background"
                  }
                  onChange={handleChange}
                  onKeyDown={(event) => handleKeyDown(event)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  data-testid="prompt-textarea"
                />
                {selectedItem.length > 0 && (
                  <div className="flex flex-row items-center flex-wrap gap-[10px] pt-4 px-3">
                    {selectedItem.map((imageStyle, index) => (
                      <button
                        key={imageStyle.label}
                        className="flex flex-row relative py-[4px] px-2 justify-center item items-center gap-[8px] rounded-[16px] border border-solid border-[#4338CA] bg-white text-[#4338CA] text-[12px] font-medium group cursor-default"
                      >
                        <span className="text-[#4338CA] text-[14px] font-medium">
                          {imageStyle.value}
                        </span>
                        <span
                          className="flex-row items-center justify-center absolute top-[-5px] shadow-xl right-[-4px] w-[13px] h-[13px] rounded-full flex-shrink-0 bg-[#fff] border border-solid border-[#2563EB] flex cursor-pointer"
                          onClick={() => onRemovedItem(imageStyle, index)}
                        >
                          <CloseIcon width={8} height={12} color="#4338CA" />
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                <ExtraAction
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  aspectRatio={aspectRatio}
                  sampleCount={sampleCount}
                  setAspectRatio={setAspectRatio}
                  setSampleCount={setSampleCount}
                />
              </div>
              {isOverMaxCharLength && (
                <span className="text-[#DC2626] font-medium">
                  Character exceeds maximum length
                </span>
              )}
            </div>
            <h3 className="text-sm font-semibold mb-2">Attach Styles</h3>

            <div className="flex flex-row items-center flex-wrap gap-[10px] mb-6">
              {displayedItems.map((imageStyle, index) => (
                <DropdownComponent
                  key={index}
                  options={imageStyle.options}
                  selectedItem={
                    (imageStyle.value as string) || imageStyle.label
                  }
                  setSelectedItem={(value) =>
                    onChangeDropdown(index, imageStyle, value)
                  }
                />
              ))}
              {handleLoadMore && (
                <button
                  className="flex flex-row items-center justify-center gap-[10px] hover:bg-[#EEF2FF] p-2 rounded transition-all duration-300"
                  onClick={handleLoadMore}
                >
                  <span className="text-[#4338CA] text-[14px] font-medium">
                    Show other styles
                  </span>
                  <span className="text-[#4338CA] flex flex-row items-center justify-center">
                    <RefreshIcon />
                  </span>
                </button>
              )}
            </div>

            {/* Pro Tips */}
            <div className="bg-[#DBEAFE] p-4 rounded-[6px] border-[0.5px] border-dashed border-[#2563EB]">
              <h3 className="text-[14px] font-semibold mb-2 text-[#2563EB] flex flex-row items-center gap-1">
                <InfoIcon />
                Pro tips to improve your results
              </h3>
              <ul className="text-[12px] text-[#2563EB] list-disc list-inside leading-[18px]">
                <li>Include subject, setting, and style for best results</li>
                <li>Add lighting details and camera specs for realism</li>
                <li>Be specific but concise with your description</li>
              </ul>
            </div>

            {/* Generate Button */}
            <div className="mt-4 flex flex-col justify-center gap-4">
              <button
                onClick={onFetch}
                disabled={disabled}
                className="px-[12px] py-[8px] bg-[#4338CA] text-white rounded-[6px] w-full text-[16px] font-semibold flex flex-row items-center justify-center gap-[8px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.25)] disabled:bg-opacity-80"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner /> Generating Image...
                  </>
                ) : (
                  <>
                    <LaunchIcon />
                    <span>Generate Image</span>
                    {!disabled && sampleCount && (
                      <span className="font-normal italic">
                        ({sampleCount.value * CREDITS_PER_IMAGE} credits)
                      </span>
                    )}
                  </>
                )}
              </button>
              {/* <button
            onClick={onTestUpgrade}
            className="px-[12px] py-[6px] bg-blue-400 text-white rounded-[6px] w-full text-[14px] font-semibold flex flex-row items-center justify-center gap-[8px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.25)]"
          >
           Purchased test button
          </button> */}

              {/* <button
                onClick={onTestFetch}
                className="px-[12px] py-[6px] bg-blue-400 text-white rounded-[6px] w-full text-[14px] font-semibold flex flex-row items-center justify-center gap-[8px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.25)]"
              >
                Test API
              </button> */}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PromptContainer;
